import React from 'react';
import { BellAlertIcon, CheckCircleIcon, ArrowRightCircleIcon } from 'components/icons';

/**
 * Renders the monitor-agent's change report.
 *
 * Field names here differ from the MonitorAgentResult type the backend still
 * declares (`title`/`summary`/`evidence` vs `message`/`data`), so this reads
 * both — the same drift that broke alert persistence on 2026-08-20.
 */
export interface MonitorPayload {
  no_changes?: boolean;
  alerts_generated?: number;
  updates_detected?: number;
  alerts?: Array<{
    type?: string;
    severity?: string;
    title?: string;
    message?: string;
    summary?: string;
    evidence?: string;
    action_required?: string;
    timestamp?: string;
  }>;
}

export function isMonitorPayload(v: any): v is MonitorPayload {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  if (typeof v.no_changes === 'boolean') return true;
  // Distinguish from other agents' arrays: monitor alerts carry a severity.
  return Array.isArray(v.alerts) && (v.alerts.length === 0 || 'severity' in (v.alerts[0] ?? {}));
}

const SEVERITY: Record<string, { chip: string; bar: string; label: string }> = {
  critical: { chip: 'bg-red-100 text-red-800', bar: 'border-red-300', label: 'Critical' },
  high: { chip: 'bg-red-100 text-red-800', bar: 'border-red-300', label: 'High' },
  warning: { chip: 'bg-amber-100 text-amber-800', bar: 'border-amber-300', label: 'Warning' },
  medium: { chip: 'bg-amber-100 text-amber-800', bar: 'border-amber-300', label: 'Medium' },
  low: { chip: 'bg-surface-sunk text-content-muted', bar: 'border-border', label: 'Low' },
  info: { chip: 'bg-blue-100 text-blue-800', bar: 'border-blue-200', label: 'Info' },
};

const humanise = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const MonitorResult: React.FC<{ data: MonitorPayload }> = ({ data }) => {
  const alerts = data.alerts ?? [];

  if (data.no_changes === true || alerts.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
        <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-green-800">No changes detected</p>
          <p className="text-sm text-green-700 mt-0.5">
            Nothing has changed on this solicitation since it was last checked.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <BellAlertIcon className="h-5 w-5 text-amber-600" aria-hidden="true" />
        <p className="text-sm font-semibold text-content">
          {alerts.length} {alerts.length === 1 ? 'change' : 'changes'} detected
        </p>
      </div>

      <ul className="space-y-3">
        {alerts.map((a, i) => {
          const sev = SEVERITY[(a.severity || 'info').toLowerCase()] || SEVERITY.info;
          const body = a.summary || a.message;
          return (
            <li key={i} className={`rounded-lg border-l-4 border border-border ${sev.bar} p-3`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-content">
                  {a.title || a.message || humanise(a.type || 'Change')}
                </p>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${sev.chip}`}
                >
                  {sev.label}
                </span>
              </div>

              {a.type && (
                <p className="text-[10px] uppercase tracking-wide text-content-muted font-semibold mt-1">
                  {humanise(a.type)}
                </p>
              )}

              {body && <p className="text-sm text-content-muted mt-1.5">{body}</p>}

              {/* Quoted source text — this is what makes the change checkable
                  rather than something the user has to take on trust. */}
              {a.evidence && (
                <blockquote className="mt-2 border-l-2 border-border pl-3 text-xs text-content-muted italic">
                  {a.evidence}
                </blockquote>
              )}

              {a.action_required && (
                <p className="flex items-start gap-1.5 text-sm text-content mt-2">
                  <ArrowRightCircleIcon
                    className="h-4 w-4 text-brand-600 flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="font-semibold text-brand-700">Do next: </span>
                    {a.action_required}
                  </span>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MonitorResult;
